<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import jwt_decode from "jwt-decode";

export default {
  data() {
    return {
      title: "Recruiter Activity",
      items: [
        {
          text: "Dashboards",
          href: "/",
        },
        {
          text: "Recruiter Activity",
          active: true,
        },
      ],
      recruitersList: [],
      selectedRecruiter: "all",
      dateRange: moment().startOf('month').format("DD MMM, YYYY") + " to " + moment().endOf('month').format("DD MMM, YYYY"),
      rangeDateconfig: {
        mode: "range",
        dateFormat: "d M, Y",
        defaultDate: moment().startOf('month').format("DD MMM, YYYY") + " to " + moment().endOf('month').format("DD MMM, YYYY")
      },
      metrics: {
        total_notes: 0,
        total_updates: 0,
        unique_prospects: 0
      },
      timeline: [],
      loading: false,
      
      // Apex Chart Series & Options
      chartSeries: [
        {
          name: "Comments/Notes",
          data: []
        },
        {
          name: "Updates/Actions",
          data: []
        }
      ],
      chartOptions: {
        chart: {
          height: 350,
          type: "area",
          toolbar: {
            show: false
          }
        },
        colors: ["#3577f1", "#405189"],
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: "smooth",
          width: 3
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [0, 100]
          }
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              colors: "#878a99",
              fontSize: "12px"
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: "#878a99",
              fontSize: "12px"
            }
          }
        },
        grid: {
          borderColor: "#e9ebec",
          strokeDashArray: 4
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          style: {
            fontSize: "12px"
          }
        }
      },
      
      // Details Modal
      showDetailsModal: false,
      selectedEvent: null,

      // Pagination
      page: 1,
      perPage: 15,
      pages: []
    };
  },
  components: {
    Layout,
    PageHeader,
    flatPickr
  },
  methods: {
    viewUserGroup(request) {
      const currentRole = jwt_decode(localStorage.getItem('jwt'));
      return currentRole.userGroup.includes(request);
    },
    formatDate(date, formatPattern) {
      return moment(date).format(formatPattern);
    },
    // Map date range selected in UI to backend parameters
    parseDateRange() {
      const dates = this.dateRange.split(" to ");
      if (dates.length === 2) {
        return {
          start: moment(dates[0], "DD MMM, YYYY").format("YYYY-MM-DD"),
          end: moment(dates[1], "DD MMM, YYYY").format("YYYY-MM-DD")
        };
      }
      return { start: "", end: "" };
    },
    async getRecruiters() {
      const payload = { token: localStorage.getItem('jwt') };
      await axios
        .post('/api/recruiters/list', payload)
        .then(response => {
          this.recruitersList = response.data;
        })
        .catch(error => {
          console.error("Error loading recruiters:", error);
        });
    },
    async fetchActivity() {
      this.loading = true;
      const dates = this.parseDateRange();
      const payload = {
        token: localStorage.getItem('jwt'),
        recruiter_email: this.selectedRecruiter,
        start_date: dates.start,
        end_date: dates.end
      };

      await axios
        .post('/api/recruiters/activity', payload)
        .then(response => {
          const res = response.data;
          this.metrics = res.metrics;
          this.timeline = res.timeline;
          this.page = 1;
          this.setPages();
          
          // Format chart data
          const dates_categories = [];
          const notes_series = [];
          const updates_series = [];

          res.chart_data.forEach(item => {
            dates_categories.push(this.formatDate(item.date, "DD MMM"));
            notes_series.push(item.notes);
            updates_series.push(item.updates);
          });

          this.chartSeries = [
            {
              name: "Comments/Notes",
              data: notes_series
            },
            {
              name: "Updates/Actions",
              data: updates_series
            }
          ];

          this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
              ...this.chartOptions.xaxis,
              categories: dates_categories
            }
          };
        })
        .catch(error => {
          console.error("Error loading activity:", error);
          Swal.fire("Oops!", "Could not load recruiter activity report.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openDetails(event) {
      this.selectedEvent = event;
      this.showDetailsModal = true;
    },
    closeDetails() {
      this.selectedEvent = null;
      this.showDetailsModal = false;
    },
    // Format JSON diff keys for changes details
    formatKey(key) {
      if (!key) return "";
      return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    },
    // Set badge style depending on activity type
    getBadgeClass(action) {
      const a = action.toLowerCase();
      if (a.includes("comment") || a.includes("note")) return "badge bg-soft-info text-info";
      if (a.includes("update") || a.includes("edit")) return "badge bg-soft-primary text-primary";
      if (a.includes("status")) return "badge bg-soft-warning text-warning";
      if (a.includes("create")) return "badge bg-soft-success text-success";
      return "badge bg-soft-secondary text-secondary";
    },
    setPages() {
      let numberOfPages = Math.ceil(this.timeline.length / this.perPage);
      this.pages = [];
      for (let index = 1; index <= numberOfPages; index++) {
        this.pages.push(index);
      }
    },
    paginate(items) {
      let page = this.page;
      let perPage = this.perPage;
      let from = page * perPage - perPage;
      let to = page * perPage;
      return items.slice(from, to);
    }
  },
  computed: {
    paginatedTimeline() {
      return this.paginate(this.timeline);
    }
  },
  async mounted() {
    await this.getRecruiters();
    await this.fetchActivity();
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Filters Section -->
    <div class="card mb-4 border-0 shadow-sm rounded-3">
      <div class="card-body bg-light p-3 rounded-3">
        <div class="row g-3 align-items-end">
          <div class="col-md-4 col-sm-6">
            <label class="form-label text-muted fw-semibold mb-1 fs-12 text-uppercase">Recruiter Selector</label>
            <select class="form-select border-0 shadow-sm" v-model="selectedRecruiter" @change="fetchActivity">
              <option value="all">All Active Recruiters</option>
              <option v-for="recruiter in recruitersList" :key="recruiter.id" :value="recruiter.email">
                {{ recruiter.name }} ({{ recruiter.email }})
              </option>
            </select>
          </div>
          <div class="col-md-4 col-sm-6">
            <label class="form-label text-muted fw-semibold mb-1 fs-12 text-uppercase">Date Range</label>
            <flat-pickr
              v-model="dateRange"
              placeholder="Select date range"
              :config="rangeDateconfig"
              class="form-control border-0 shadow-sm"
              @change="fetchActivity"
            ></flat-pickr>
          </div>
          <div class="col-md-4 col-sm-12 text-md-end text-sm-start mt-md-0 mt-3">
            <button class="btn btn-primary px-4 shadow-sm" @click="fetchActivity" :disabled="loading">
              <i class="ri-refresh-line align-bottom me-1" :class="{'spin-icon': loading}"></i>
              Refresh Report
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Metrics Cards -->
    <div class="row mb-4">
      <div class="col-md-4">
        <div class="card card-animate border-0 shadow-sm rounded-3 h-100 position-relative overflow-hidden">
          <div class="card-body p-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <p class="text-uppercase fw-semibold text-muted mb-2 fs-12">Total Comments & Notes</p>
                <h2 class="mb-0 text-info fw-bold">{{ metrics.total_notes }}</h2>
              </div>
              <div class="flex-shrink-0">
                <div class="avatar-sm bg-soft-info text-info rounded-circle d-flex align-items-center justify-content-center fs-24">
                  <i class="ri-chat-3-line"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="card-border bg-info position-absolute bottom-0 start-0 end-0" style="height: 3px;"></div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card card-animate border-0 shadow-sm rounded-3 h-100 position-relative overflow-hidden">
          <div class="card-body p-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <p class="text-uppercase fw-semibold text-muted mb-2 fs-12">Total Status Updates & Actions</p>
                <h2 class="mb-0 text-primary fw-bold">{{ metrics.total_updates }}</h2>
              </div>
              <div class="flex-shrink-0">
                <div class="avatar-sm bg-soft-primary text-primary rounded-circle d-flex align-items-center justify-content-center fs-24">
                  <i class="ri-edit-2-line"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="card-border bg-primary position-absolute bottom-0 start-0 end-0" style="height: 3px;"></div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card card-animate border-0 shadow-sm rounded-3 h-100 position-relative overflow-hidden">
          <div class="card-body p-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <p class="text-uppercase fw-semibold text-muted mb-2 fs-12">Unique Candidates Worked</p>
                <h2 class="mb-0 text-warning fw-bold">{{ metrics.unique_prospects }}</h2>
              </div>
              <div class="flex-shrink-0">
                <div class="avatar-sm bg-soft-warning text-warning rounded-circle d-flex align-items-center justify-content-center fs-24">
                  <i class="ri-user-shared-line"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="card-border bg-warning position-absolute bottom-0 start-0 end-0" style="height: 3px;"></div>
        </div>
      </div>
    </div>

    <!-- Chart Breakdown -->
    <div class="card mb-4 border-0 shadow-sm rounded-3">
      <div class="card-header border-0 bg-transparent align-items-center d-flex pt-4 px-4 pb-0">
        <h4 class="card-title mb-0 flex-grow-1 fw-bold">Daily Activity Breakdown</h4>
      </div>
      <div class="card-body px-4">
        <div v-if="loading" class="d-flex justify-content-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading Chart...</span>
          </div>
        </div>
        <apexchart
          v-else
          type="area"
          height="350"
          :options="chartOptions"
          :series="chartSeries"
        ></apexchart>
      </div>
    </div>

    <!-- Timeline Event Log -->
    <div class="card border-0 shadow-sm rounded-3 mb-4">
      <div class="card-header border-0 bg-transparent align-items-center d-flex pt-4 px-4 pb-0">
        <h4 class="card-title mb-0 flex-grow-1 fw-bold">Detailed Activity Logs</h4>
      </div>
      <div class="card-body px-4">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading logs...</span>
          </div>
        </div>
        <div v-else-if="!timeline.length" class="text-center py-5 bg-light rounded-3">
          <i class="ri-history-line fs-36 text-muted mb-3"></i>
          <p class="text-muted fs-14">No activities found for this recruiter and date range.</p>
        </div>
        <div v-else>
          <div class="table-responsive">
            <table class="table align-middle table-hover mb-0">
              <thead class="table-light text-muted">
                <tr>
                  <th scope="col" style="width: 15%;">Date & Time</th>
                  <th scope="col" style="width: 20%;">Recruiter</th>
                  <th scope="col" style="width: 15%;">Action</th>
                  <th scope="col" style="width: 20%;">Candidate</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="event in paginatedTimeline" :key="event.id" class="cursor-pointer" @click="openDetails(event)">
                  <td class="text-muted fs-12">{{ formatDate(event.createdOn, "DD MMM YYYY, hh:mm A") }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="avatar-xxs me-2 rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center fw-bold">
                        {{ event.recruiter_name ? event.recruiter_name.charAt(0).toUpperCase() : 'S' }}
                      </div>
                      <div>
                        <span class="fw-medium text-dark d-block">{{ event.recruiter_name }}</span>
                        <small class="text-muted fs-10">{{ event.recruiter_email }}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span :class="getBadgeClass(event.action_type)">
                      {{ event.action_type }}
                    </span>
                  </td>
                  <td>
                    <router-link :to="'/agents/List?agent_id=' + event.candidate_id" class="link-primary fw-medium d-block" @click.stop>
                      {{ event.candidate_name }}
                    </router-link>
                    <small class="text-muted fs-10">{{ event.candidate_email }}</small>
                  </td>
                  <td class="text-truncate text-muted fs-12" style="max-width: 250px;">
                    {{ event.description }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination Section -->
          <div class="d-flex justify-content-between align-items-center mt-4" v-if="timeline.length > 0">
            <div class="text-muted fs-13">
              Showing <span class="fw-semibold">{{ (page - 1) * perPage + 1 }}</span> to <span class="fw-semibold">{{ Math.min(page * perPage, timeline.length) }}</span> of <span class="fw-semibold">{{ timeline.length }}</span> activities
            </div>
            <div class="pagination-wrap hstack gap-2" v-if="pages.length > 1">
              <a
                  class="page-item pagination-prev"
                  :class="{ disabled: page === 1 }"
                  href="javascript:void(0);"
                  @click="page > 1 && page--"
              >
                Previous
              </a>
              <ul class="pagination listjs-pagination mb-0">
                <li
                    :class="{ active: pageNumber === page }"
                    v-for="(pageNumber, index) in pages.slice(Math.max(0, page - 3), Math.min(pages.length, page + 2))"
                    :key="index"
                    @click="page = pageNumber"
                >
                  <a class="page" href="javascript:void(0);">{{ pageNumber }}</a>
                </li>
              </ul>
              <a
                  class="page-item pagination-next"
                  :class="{ disabled: page === pages.length }"
                  href="javascript:void(0);"
                  @click="page < pages.length && page++"
              >
                Next
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Details Modal -->
    <div class="modal fade show" v-if="showDetailsModal" style="display: block; background: rgba(0,0,0,0.5);" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content border-0 shadow-lg rounded-3">
          <div class="modal-header border-bottom-flat pt-3 px-4 pb-2">
            <h5 class="modal-title fw-bold">Activity Detail View</h5>
            <button type="button" class="btn-close" @click="closeDetails" aria-label="Close"></button>
          </div>
          <div class="modal-body px-4 py-3" v-if="selectedEvent">
            <div class="d-flex align-items-center mb-4 bg-light p-3 rounded-3">
              <div class="avatar-sm rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center fw-bold fs-20 me-3">
                {{ selectedEvent.recruiter_name.charAt(0).toUpperCase() }}
              </div>
              <div>
                <h5 class="mb-0 fw-semibold text-dark">{{ selectedEvent.recruiter_name }}</h5>
                <small class="text-muted">{{ formatDate(selectedEvent.createdOn, "MMMM DD, YYYY hh:mm A") }}</small>
              </div>
            </div>

            <h6 class="text-muted text-uppercase fs-11 fw-bold mb-2">Target Candidate</h6>
            <p class="text-dark mb-4 fw-medium">
              <router-link :to="'/agents/List?agent_id=' + selectedEvent.candidate_id" class="link-primary" @click="closeDetails">
                {{ selectedEvent.candidate_name }}
              </router-link>
              <span class="text-muted fs-12 ml-2" v-if="selectedEvent.candidate_email">({{ selectedEvent.candidate_email }})</span>
            </p>

            <h6 class="text-muted text-uppercase fs-11 fw-bold mb-2">Description / Log Message</h6>
            <div class="p-3 bg-light rounded-3 text-muted fs-13 mb-4" style="white-space: pre-line;">
              {{ selectedEvent.description }}
            </div>

            <!-- Changes JSON Diffs -->
            <div v-if="selectedEvent.changes && Object.keys(selectedEvent.changes).length > 0">
              <h6 class="text-muted text-uppercase fs-11 fw-bold mb-2">Changes Logged</h6>
              <div class="table-responsive border rounded-3 p-2">
                <table class="table table-sm table-borderless mb-0">
                  <thead>
                    <tr class="fs-11 text-muted border-bottom">
                      <th>Attribute</th>
                      <th>Previous State</th>
                      <th>New State</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(change, key) in selectedEvent.changes" :key="key" class="fs-12">
                      <td class="fw-semibold text-capitalize text-dark">{{ formatKey(key) }}</td>
                      <td class="text-danger text-decoration-line-through">{{ change.old }}</td>
                      <td class="text-success fw-medium">{{ change.new }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top-flat px-4 py-3">
            <button type="button" class="btn btn-light px-4" @click="closeDetails">Close</button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.card-animate {
  transition: all 0.3s ease;
}
.card-animate:hover {
  transform: translateY(-4px);
}
.spin-icon {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.cursor-pointer {
  cursor: pointer;
}
.border-bottom-flat {
  border-bottom: 1px solid #f3f3f3;
}
.border-top-flat {
  border-top: 1px solid #f3f3f3;
}
</style>
